<template>
  <v-app>
    <v-app-bar app fixed v-if="isOutside">
      <v-toolbar-title class="headline text-uppercase">
        <i class="logo"></i>
      </v-toolbar-title>
      <v-spacer></v-spacer>

      <!-- app-bar 追加項目 -->
      <portal-target name="app-bar-append"></portal-target>
    </v-app-bar>
    <v-app-bar app fixed clipped-left v-else>
      <v-toolbar-title class="headline text-uppercase">
        <i class="logo"></i>
      </v-toolbar-title>
      <v-toolbar-items>
        <v-row
            dense
            align="center"
            justify="center">
          <v-col>
            <!-- パンくずリスト -->
            <el-breadcrumb separator="/" :class="{isEditable}">
              <el-breadcrumb-item :to="{name: 'Home'}" :disabled="$route.name === 'Home'">
                <i class="el-icon-s-home"></i>
              </el-breadcrumb-item>
              <el-breadcrumb-item v-for="bc in breadcrumbItems" :key="bc.to.name" :to="bc.to">
                {{ bc.label }}
              </el-breadcrumb-item>
            </el-breadcrumb>
          </v-col>
        </v-row>
      </v-toolbar-items>

      <v-spacer></v-spacer>

      <!-- 通知 -->
      <el-popover
          v-model="notificationPopoverVisible"
          placement="bottom"
          @created="handleNotificationPopupCreated">
        <v-badge
            slot="reference"
            :value="!!globalNotification"
            color="red"
            offset-x="20"
            offset-y="20"
            dot
            overlap>
          <v-btn icon>
            <v-icon>mdi-bell</v-icon>
          </v-btn>
        </v-badge>
        <template #default>
          <span class="notification" v-if="!!globalNotification">{{ globalNotification }}</span>
          <span class="no-notification" v-else>通知はありません</span>
        </template>
      </el-popover>

      <!-- アカウントメニュー -->
      <v-menu close-on-click bottom offset-y>
        <template #activator="{on, attrs}">
          <v-btn v-bind="attrs" v-on="on" class="account-button" text>
            <v-icon>mdi-account</v-icon>
            <span class="account-name">{{ accountName }}</span>
            <span class="terminal-name">{{ currentTerminalName }}</span>
          </v-btn>
        </template>

        <v-list>
          <v-list-item @click="ownerInfoDialog = 'self'">事業者情報</v-list-item>
          <v-list-item @click="terminalSwitchDialog = true" v-if="availableTerminals.length >= 2">サイト切替</v-list-item>
          <v-list-item @click="handleSignOut">ログアウト</v-list-item>
        </v-list>
      </v-menu>

      <!-- app-bar 追加項目 -->
      <portal-target name="app-bar-append"></portal-target>
    </v-app-bar>

    <!-- ナビゲーションバー -->
    <v-navigation-drawer
        v-model="showDrawer"
        :mini-variant="miniDrawer"
        v-if="renderDrawer"
        color="blue-grey lighten-5"
        permanent app clipped>
      <v-list-item @click="miniDrawer = true" v-if="!miniDrawer">
        <v-list-item-icon>
          <v-icon>mdi-chevron-double-left</v-icon>
        </v-list-item-icon>
        <v-list-item-content></v-list-item-content>
      </v-list-item>

      <v-list-item @click="miniDrawer = false" v-else>
        <v-list-item-icon>
          <v-icon>mdi-chevron-double-right</v-icon>
        </v-list-item-icon>
        <v-list-item-content></v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>
      <navigation-list :mini="miniDrawer"></navigation-list>
    </v-navigation-drawer>

    <!-- アプリケーション -->
    <v-main>
      <!-- アプリケーションヘッダ -->
      <div class="app-header-padding" :style="{height: appHeader.height}"></div>
      <div class="app-header-container"
           :style="{
             paddingLeft: mainPadding.left,
             paddingTop: mainPadding.top,
             paddingRight: mainPadding.right
           }">
        <portal-target name="app-header" class="app-header" multiple></portal-target>
      </div>

      <!-- アプリケーション本体 -->
      <router-view />

      <!-- 下部フェード -->
      <div class="main-bottom-fade" :style="{height: mainBottomFadeHeight, paddingLeft: mainPadding.left}"></div>
    </v-main>

    <!-- フッター -->
    <v-footer app>
      <span class="">&copy; {{ currentYear }} IVI</span>
    </v-footer>

    <!-- グローバルスナックバー -->
    <v-snackbar
        v-model="snackbar.visible"
        absolute
        top
        :color="snackbar.color || 'success'"
        :timeout="snackbar.timeout"
        :style="{cursor: 'default', position: 'fixed'}"
        @click.native="snackbar.visible = false">
      <v-icon v-show="snackbar.loading" style="color: currentColor; text-align: left; margin-right: 10px;">
        el-icon-loading
      </v-icon>
      <span :style="{flexGrow: 1, whiteSpace: 'pre-wrap'}">{{ snackbar.message }}</span>
    </v-snackbar>

    <!-- 事業者情報ダイアログ -->
    <owner-info-dialog
        v-model="ownerInfoDialog">
    </owner-info-dialog>

    <!-- サイト切替ダイアログ -->
    <terminal-switch-dialog
        v-model="terminalSwitchDialog"
        @apply="handleTerminalSwitched">
    </terminal-switch-dialog>

    <!-- 起動直後のログインチェック中に出すスプラッシュ -->
    <transition name="el-loading-fade">
      <v-overlay v-if="splashVisible" color="white" opacity="1">
        <v-progress-circular indeterminate size="64" color="light-blue"></v-progress-circular>
      </v-overlay>
    </transition>

    <!-- HCM からのイベントを受け付ける -->
    <hcm-subscriber></hcm-subscriber>
  </v-app>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import {mapActions, mapGetters, mapMutations} from "vuex";
import NavigationList from "@/components/organisms/NavigationList.vue";
import PagePermissionMixin from "@/utils/PermissionMixin";
import {safeMeta} from "@/router/Meta";
import OwnerInfoDialog from "@/components/organisms/OwnerInfoDialog.vue";
import HcmSubscriber from "@/components/atoms/HcmSubscriber";
import TerminalSwitchDialog from "@/components/organisms/TerminalSwitchDialog.vue";
import Swal from "sweetalert2";
import PageStoreMixin, {mapPageStores} from "@/utils/PageStoreMixin";
import {DateTime} from "luxon";

/**
 * スクロールバーの幅を求める
 *
 * @return {number} スクロールバーの幅 (px)
 */
function getScrollbarWidth() {
  let element = document.createElement('div');
  element.style.visibility = 'hidden';
  element.style.overflow = 'scroll';
  document.body.appendChild(element);
  const scrollbarWidth = element.offsetWidth - element.clientWidth;
  document.body.removeChild(element);

  return scrollbarWidth;
}

export default {
  name: 'app',
  components: {TerminalSwitchDialog, HcmSubscriber, OwnerInfoDialog, NavigationList},
  mixins: [PathInfoMixin, PagePermissionMixin, PageStoreMixin],
  data() {
    return {
      showDrawer: true,
      splashVisible: true,
      notificationPopoverVisible: false,
      notificationPopover: null,
      ownerInfoDialog: false,
      terminalSwitchDialog: false,
      mainResizeObserver: new ResizeObserver(this.handleMainResize),
      mainBottomFadeHeight: 0,
      mainPadding: {
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
      },
      mainBounds: {
        width: 0,
        height: 0,
      },
      mainScroll: {
        y: 0,
        x: 0,
        height: 0,
        width: 0,
        moveTo: options => {
          window.scrollTo({
            behavior: 'smooth',
            ...options
          })
        }
      },
      appHeaderResizeObserver: new ResizeObserver(this.handleAppHeaderResize),
      appHeader: {
        height: 0
      }
    };
  },
  provide() {
    return {
      mainPadding: this.mainPadding,
      mainBounds: this.mainBounds,
      mainScroll: this.mainScroll,
      appHeader: this.appHeader,
    }
  },
  created() {
    // CSRF の取得
    this.fetchCSRFToken()
        .then(() => {
          // 起動直後のログインチェック
          return this.fetchLoginAccount()
        })
        .then(() => {
          // 起動直後のパーミッションチェック
          const fallback = this.hasFallbackPage(this.$route);
          if(!fallback) {
            this.showSnackbar({
              color: 'warn',
              message: `${safeMeta(this.$route).label} を表示する権限がありませんでした。`,
            });
            return this.$router.replace({name: 'Home'});
          } else if(fallback.hop) {
            this.showSnackbar({
              color: 'warn',
              message:
                  `${safeMeta(fallback.initialRoute).label} を表示する権限がないため移動はキャンセルされました。\n` +
                  `代わりに ${safeMeta(fallback.route).label} を表示します。`,
            });
            return this.$router.replace(fallback.location);
          }
        }, () => {
          this.navigateSignIn();
        })
        .finally(() => {
          this.splashVisible = false;
        });

    // 未ログイン時の画面遷移を防ぐ
    this.$router.beforeEach((to, from, next) => {
      const account = this.$store.getters['hcs/account'];
      if (to.name === 'SignIn') {
        return next();
      } else if (account.user && account.owner) {
        const fallback = this.hasFallbackPage(to);
        if(!fallback) {
          this.showSnackbar({
            color: 'warn',
            message: `${safeMeta(to).label} を表示する権限がありませんでした。`,
          });
        } else if(fallback.hop) {
          this.showSnackbar({
            color: 'warn',
            message:
                `${safeMeta(fallback.initialRoute).label} を表示する権限がないため移動はキャンセルされました。\n` +
                `代わりに ${safeMeta(fallback.route).label} を表示します。`,
          });
          return next(fallback.location);
        } else {
          return next();
        }
      }
      return next(false);
    });
  },
  mounted() {
    // app 固有の動的スタイルシートを追加
    const rule = `.v-application { --scrollbar-width: ${getScrollbarWidth()}px; }`;
    const existingStyle = document.querySelector("#app-dynamic-stylesheet");
    if(existingStyle) {
      existingStyle.innerHTML = rule;
    } else {
      const newStyle = document.createElement('style');
      newStyle.id = 'app-dynamic-stylesheet';
      newStyle.type = "text/css";
      newStyle.innerHTML = rule;
      document.querySelector('head').appendChild(newStyle);
    }

    // v-main が最下部までスクロールされていない場合に、画面下部を陰らせるエフェクトを追加
    window.document.addEventListener('scroll', this.handleMainResize);
    window.addEventListener('resize', this.handleMainResize);
    this.mainResizeObserver.observe(this.$el.querySelector('.v-main'));
    this.$nextTick(this.handleMainResize);

    // App Header のリサイズを検出する
    this.$nextTick(() => {
      this.appHeaderResizeObserver.observe(this.$el.querySelector('.app-header'));
      this.$nextTick(this.handleAppHeaderResize);
    });

    // クローバル通知を監視してポップアップを表示したり、ポップアップの位置を更新する
    this.$watch(
        _ => ([this.globalNotification, this.isOutside, this.accountName]),
        ([globalNotification, isOutside, accountName], oldValues) => {
          if(Array.isArray(oldValues)
              && globalNotification === oldValues[0]
              && isOutside === oldValues[1]
              && accountName === oldValues[2]) {
            return;
          }
          if(isOutside) {
            if(this.notificationPopoverVisible) {
              this.notificationPopover?.doDestroy(true);
              this.notificationPopoverVisible = false;
            }
          } else {
            if(this.notificationPopoverVisible) {
              this.notificationPopover?.updatePopper();
            }
            this.notificationPopoverVisible = !!globalNotification;
          }
        }, {
          immediate: true
        });
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    ...mapPageStores({
      useEasyHome: {alt: true, key: 'app.useEasyHome'},
      miniDrawer: {alt: false, key: 'app.miniDrawer'},
    }),
    /** ツールバーの右上に出すアカウント名 */
    accountName() {
      return `${this.account.user?.name} @ ${this.account.owner?.name}`;
    },
    currentTerminalName() {
      return this.account.user?.terminal?.name || '';
    },
    availableTerminals() {
      return this.account.user?.availableTerminals || [];
    },
    isOutside() {
      return this.$route.name === 'SignIn';
    },
    renderDrawer() {
      if(this.isOutside || this.useEasyHome) {
        return false;
      }

      let current = this.$route;
      while (current.name !== 'Home') {
        const currentMeta = safeMeta(current);
        if(currentMeta.hideDrawer === true) {
          return false;
        }
        if(!currentMeta.parent) {
          break;
        }
        ({route: current} = this.$router.resolve({name: currentMeta.parent}));
      }
      return true;
    },
    breadcrumbItems() {
      const result = []
      let current = this.$route;
      while (current.name !== 'Home') {
        const currentMeta = safeMeta(current);
        result.push({
          label: currentMeta.label,
          to: {name: current.name}
        });
        if(!currentMeta.parent) {
          break;
        }
        ({route: current} = this.$router.resolve({name: currentMeta.parent}));
      }
      return result.reverse();
    },
    snackbar() {
      return this.$store.state.snackbar;
    },
    globalNotification() {
      return this.$store.state.globalParams.notification;
    },
    globalAppStatus() {
      return this.$store.state.globalParams.status;
    },
    clientRevision() {
      return this.$store.state.clientParams.revision;
    },
    currentYear() {
      const lastModified = Date.parse(document.lastModified);
      return DateTime.fromMillis(lastModified).toFormat('yyyy');
    },
  },
  methods: {
    ...mapActions({
      signOut: 'hcs/signOut',
      fetchLoginAccount: 'hcs/fetchLoginAccount',
      fetchCSRFToken: "hcs/fetchCSRFToken",
    }),
    ...mapMutations({
      showSnackbar: 'showSnackbar',
      setLoginUser: 'hcs/setLoginUser',
    }),
    handleAppHeaderResize() {
      this.$nextTick(() => {
        const style = window.getComputedStyle(this.$el.querySelector('.app-header'));
        this.appHeader.height = `calc(${style.height} + 1px)`;
      });
    },
    handleMainResize() {
      const main = this.$el.querySelector('.v-main');
      const body = window.document.body;
      const html = window.document.documentElement;

      const scrollTop = body.scrollTop || html.scrollTop;
      const scrollLength = html.scrollHeight - html.clientHeight;
      this.mainScroll.y = scrollTop;
      this.mainScroll.height = scrollLength;

      const ratio = Math.max(1, scrollLength / 200);
      this.mainBottomFadeHeight = Math.min(40, (scrollLength - scrollTop) / ratio) + 'px';

      const mainStyle = main?.style;
      this.mainPadding.top = mainStyle?.paddingTop ?? 0;
      this.mainPadding.left = mainStyle?.paddingLeft ?? 0;
      this.mainPadding.bottom = mainStyle?.paddingBottom ?? 0;
      this.mainPadding.right = mainStyle?.paddingRight ?? 0;

      const clientWidth = Math.max(html.clientWidth || 0, window.innerWidth || 0);
      const clientHeight = Math.max(html.clientHeight || 0, window.innerHeight || 0);
      this.mainBounds.width = `${clientWidth}px`;
      this.mainBounds.height = `${clientHeight}px`;
    },
    /**
     * サインイン画面に遷移します
     *
     * @param backToHome {boolean} ホームに戻らせる場合 true そうでない場合 false
     */
    navigateSignIn(backToHome = false) {
      if (this.$route.name !== 'SignIn') {
        let next = backToHome ? undefined : this.$route.fullPath;
        if(next === '/') {
          next = undefined;
        }

        this.$router.replace({
          name: 'SignIn',
          query: {
            next
          }
        });
      }
    },
    /**
     * 指定された location に遷移します。
     * location が現在表示されているルートと同一だった場合はページが再読込されます。
     *
     * @param location 遷移先。 null の場合は今のルート
     * @param interceptor {() => Promise<any> | null} 切替中に実行する非同期インターセプタ
     * @param blankLocation Blank ページのロケーション。 null の場合は今のルート
     * @return {Promise<any> | undefined} インターセプタの返値を返す Promise
     */
    async navigate(location, interceptor, blankLocation) {
      let result = undefined;
      const currentLocation = {...this.$route};
      if(!location) {
        location = currentLocation;
      }
      if(interceptor || this.$route.name === location.name) {
        await this.$router.replace({
          name: 'Blank',
          ...blankLocation,
        });
      }
      if(interceptor) {
        try {
          result = await interceptor();
        } catch (e) {
          await this.$router.replace(currentLocation);
          return;
        }
      }
      await this.$router.replace(location);
      return result;
    },
    /**
     * ターミナル切替された後に呼び出されます
     * ホーム直下のルートまで戻されます。
     */
    async handleTerminalSwitched(terminalId) {
      let current = this.$route;
      while (current.name !== 'Home') {
        const currentMeta = safeMeta(current);
        if(currentMeta.parent === 'Home') {
          break;
        }
        ({route: current} = this.$router.resolve({name: currentMeta.parent}));
      }

      const loginUser = await this.navigate(current, async _ => {
        const {data: loginUser} = await this.hcsClient.patch(`/users/${this.account.user.id}`, {
          terminalId: terminalId
        });
        this.setLoginUser({loginUser});
        return loginUser;
      }, {
        params: {
          loading: true,
          loadingText: 'サイト切替中...',
        }
      });

      await Swal.fire({
        title: 'サイトの切替',
        html: `サイトを「${loginUser.terminal.name}」に切り替えました。`,
        icon: 'success',
        showCloseButton: false,
        showCancelButton: false,
        focusConfirm: false,
        didOpen(popup) {
          popup.parentElement.style.zIndex = "9999";
        }
      });
    },
    /**
     * サインアウトボタンが押下された場合に呼び出されます
     */
    handleSignOut() {
      this.signOut();
      this.navigateSignIn(true);
    },
    /**
     * 通知ポップアップが作成された場合に呼び出されます
     * @param popover 通知ポップアップ
     */
    handleNotificationPopupCreated(popover) {
      this.notificationPopover = popover;
    }
  },
  watch: {
    '$route': {
      immediate: true,
      handler() {
        document.title = `CIOF - ${safeMeta(this.$route).label}`;
      }
    },
    globalAppStatus: {
      immediate: true,
      handler(status) {
        if(status === 'closed') {
          this.navigateSignIn();
        }
      }
    },
    clientRevision(newRevision, oldRevision) {
      if(oldRevision && oldRevision !== newRevision) {
        window.location.reload();
      }
    }
  }
}
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

.v-card__subtitle, .v-card__text, .v-card__title {
  text-align: start;
}
</style>

<style lang="scss" src="./vuetify-custom.scss"></style>
<style lang="scss" src="./theme-global.scss"></style>
<style lang="css" src="./assets/style.css"></style>

<style lang="scss" scoped>
.v-toolbar {
  flex-grow: 0;
  overflow: hidden;

  .logo {
    display: inline-block;
    width: 100px;
    height: 54px;
    vertical-align: middle;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    background-image: url("assets/logo-small.svg");
  }

  .v-btn.account-button {
    text-transform: none;
    padding-top: 16px;
    top: -6px;
    padding-bottom: 4px;
    height: auto;

    .account-name {
      margin-left: 5px;
    }

    .terminal-name {
      position: absolute;
      font-size: 12px;
      //bottom: -15px;
      top: -16px;
      color: rgba(0, 0, 0, 0.64);
      right: 0;
    }
  }
}

.v-snack {
  z-index: 3000;
}

.el-breadcrumb {
  margin-left: 20px;
  font-size: 16px;

  ::v-deep {
    .el-breadcrumb__separator {
      padding: 0;
      margin: 0 5px;
      color: #6b6d71;
    }

    .el-breadcrumb__inner {
      color: #252525;
    }
  }

  &.editable {
    ::v-deep {
      .el-breadcrumb__inner:hover {
        color: #0066ff;
      }
    }
  }
}

// (主に Windows 環境で) v-navigation-drawer が mini-variant の場合のスクロールバーによる表示崩れを修正する
.v-navigation-drawer.v-navigation-drawer--mini-variant ::v-deep .v-navigation-drawer__content {
  margin-right: calc(0px - var(--scrollbar-width));
}

::v-deep .v-content__wrap {
  padding-top: 10px;
}

// フッター
.v-footer {
  border-top: 1px solid #999 !important;
}

/** 通知 */
.notification {
  display: block;
  font-size: 120%;
  color: black;
  white-space: pre-wrap;
  padding: .3rem;
}

.no-notification {
  padding: 1rem;
}

// v-main が最下部までスクロールされていない場合に、画面下部を陰らせるエフェクトを追加
.main-bottom-fade {
  content: "";
  position: fixed;
  z-index: 3;
  left: 0;
  right: 0;
  pointer-events: none;
  bottom: 37px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(56, 23, 23, 0.2))
}
</style>

<style lang="scss" scoped>
.app-header-padding {
  box-sizing: border-box;
}

.app-header-container {
  box-sizing: content-box;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: 3;
  backdrop-filter: blur(6px);
  border-bottom: 1px solid #BBB6;

  .app-header {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    background-color: rgba(255, 255, 255, 0.5);

    & > ::v-deep *:first-child {
      margin-top: 5px;
    }
  }
}
</style>
